import { useState, useEffect, useCallback } from 'react'
import type { Node } from '@/service/graph'
import { parseName } from '@/service/graph'

export function useNodeSearch(
  nodes: Map<string, Node>,
  debouncedSearchTerm: string
) {
  const [matchingNodeIds, setMatchingNodeIds] = useState<string[]>([])
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)

  const performSearch = useCallback(
    (term: string): string[] => {
      if (!term.trim()) return []

      const lowerTerm = term.toLowerCase()
      const matches: string[] = []

      nodes.forEach((node, nodeId) => {
        let searchableText = ''

        switch (node.type) {
          case 'symbol': {
            // Prefer symbol_type (ML detected type) over name for searching
            // Note: symbol_type can be the literal string 'null', not actual null
            const nameToUse =
              node.symbol_type && node.symbol_type !== 'null'
                ? node.symbol_type
                : node.name
            const { label, category } = parseName(nameToUse)
            // Include the classification + user label so a manually-classified
            // object (e.g. a traced "Pit" tank) is findable by its Type.
            const classification = `${node.pnid_level_i ?? ''} ${node.pnid_level_ii ?? ''} ${node.pnid_level_iii ?? ''} ${node.label ?? ''}`
            searchableText =
              `${nameToUse} ${label} ${category} ${classification}`.toLowerCase()
            break
          }
          case 'tag':
            searchableText =
              `${node.content} ${node.pnid_level_i ?? ''} ${node.pnid_level_ii ?? ''} ${node.pnid_level_iii ?? ''} ${node.label ?? ''}`.toLowerCase()
            break
          case 'connection':
            return
        }

        if (searchableText.includes(lowerTerm)) {
          matches.push(nodeId)
        }
      })

      return matches
    },
    [nodes]
  )

  // Preserve matchingNodeIds reference when the result content is unchanged.
  // Without this, unrelated node-map updates rerun the search and hand out a
  // fresh array, which re-fires downstream effects and snaps the highlight
  // back to the search match after the user has navigated elsewhere.
  useEffect(() => {
    const matches = performSearch(debouncedSearchTerm)
    setMatchingNodeIds((prev: string[]) => {
      if (
        prev.length === matches.length &&
        prev.every((id: string, i: number) => id === matches[i])
      ) {
        return prev
      }
      return matches
    })
  }, [debouncedSearchTerm, performSearch])

  // Reset index only when the user changes the search term, not when
  // upstream node updates cause the search to rerun.
  useEffect(() => {
    setCurrentMatchIndex(0)
  }, [debouncedSearchTerm])

  const goToNext = useCallback(() => {
    if (matchingNodeIds.length === 0) return
    setCurrentMatchIndex(
      (prev: number) => (prev + 1) % matchingNodeIds.length
    )
  }, [matchingNodeIds.length])

  const goToPrevious = useCallback(() => {
    if (matchingNodeIds.length === 0) return
    setCurrentMatchIndex(
      (prev: number) => (prev - 1 + matchingNodeIds.length) % matchingNodeIds.length
    )
  }, [matchingNodeIds.length])

  // FS-657: jump straight to a match the user picked out of the list, rather
  // than stepping there with next/previous.
  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= matchingNodeIds.length) return
    setCurrentMatchIndex(index)
  }, [matchingNodeIds.length])

  return {
    matchingNodeIds,
    currentMatchIndex,
    goToNext,
    goToPrevious,
    goTo,
  }
}
